import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Eye, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Banner shown at the top of portal pages when an Admin has logged in
 * via a client-branded login (/c/{code}/login). Reads the
 * sessionStorage.viewAsClientId flag set by `BrandedLogin.js`.
 *
 * Provides:
 *  • Visual "viewing as <Company>" indicator
 *  • One-click exit back to /clients (admin console)
 *
 * The flag is sessionStorage-scoped so it auto-clears when the tab is
 * closed, preventing accidental persistent impersonation.
 */
export default function ViewAsBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [companyName, setCompanyName] = useState(
    () => sessionStorage.getItem("viewAsClientName") || "");
  const [clientId, setClientId] = useState(
    () => sessionStorage.getItem("viewAsClientId") || "");

  // Re-read when route changes — admin might have navigated into portal
  // from a different entry point.
  useEffect(() => {
    setCompanyName(sessionStorage.getItem("viewAsClientName") || "");
    setClientId(sessionStorage.getItem("viewAsClientId") || "");
  }, [location.pathname]);

  // Auto-close the audit session if the tab is closed or navigated away
  // before the user clicks Exit. `navigator.sendBeacon` is delivered even
  // after the page unloads, and sends same-origin cookies — so the
  // existing admin-auth gate on /impersonation/end keeps working.
  useEffect(() => {
    if (!clientId) return undefined;
    const handler = () => {
      const eventId = sessionStorage.getItem("viewAsEventId");
      if (!eventId) return;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/impersonation/end`;
        const blob = new Blob(
          [JSON.stringify({ event_id: eventId, exit_reason: "tab_closed" })],
          { type: "application/json" });
        navigator.sendBeacon?.(url, blob);
      } catch { /* best-effort */ }
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, [clientId]);

  const exit = useCallback(async () => {
    const eventId = sessionStorage.getItem("viewAsEventId");
    if (eventId) {
      try {
        await api.post("/admin/impersonation/end",
          { event_id: eventId, exit_reason: "user" });
      } catch { /* audit failure non-blocking */ }
    }
    sessionStorage.removeItem("viewAsClientId");
    sessionStorage.removeItem("viewAsClientName");
    sessionStorage.removeItem("viewAsEventId");
    setCompanyName(""); setClientId("");
    navigate("/clients");
  }, [navigate]);

  // Only show for admin users while in view-as mode. The route check
  // covers both the legacy /portal flow and the new /clients/{id}
  // workspace flow used by the branded-login redirect.
  const onClientWorkspace = location.pathname.startsWith("/portal")
    || location.pathname.startsWith("/clients/");
  if (!user || user.role !== "admin" || !onClientWorkspace || !clientId) {
    return null;
  }

  return (
    <div
      className="bg-amber-500 text-zinc-900 px-4 py-2.5 flex items-center justify-between gap-3 shadow-md border-b-2 border-amber-600 print:hidden"
      data-testid="view-as-banner">
      <div className="flex items-center gap-3 min-w-0">
        <Eye className="h-4 w-4 flex-shrink-0" />
        <div className="text-xs sm:text-sm font-display font-bold tracking-tight truncate">
          Viewing portal as
          <span className="font-mono ml-1.5 px-1.5 py-0.5 rounded-sm bg-amber-100 border border-amber-700/30">
            {companyName || clientId}
          </span>
        </div>
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.15em] text-amber-900/80 font-semibold">
          You are an Administrator · live client data, read-only view
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button size="sm" variant="outline" onClick={exit}
          className="rounded-sm h-7 px-2 bg-white text-zinc-900 border-amber-700/30 hover:bg-amber-50 text-[11px]"
          data-testid="view-as-exit">
          <X className="h-3 w-3 mr-1" /> Exit view-as mode
        </Button>
        <a href="/clients"
          className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-amber-900 hover:text-zinc-900"
          data-testid="view-as-admin-link">
          Admin Console <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>
    </div>
  );
}
